import React, { useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import Logo from '../components/Logo';

const LoginPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const from = location.state?.from?.pathname || '/';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const user = await login(email, password);
      toast.success(`Welcome back, ${user.name}!`);
      
      if (user.is_admin) {
        navigate('/admin');
      } else {
        navigate(from);
      }
    } catch (error) {
      if (error.message && error.message.includes('postMessage')) return;
      toast.error(error.message || 'Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center py-12 px-4 pb-56">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex justify-center mb-4">
            <Logo size="large" showTagline={true} />
          </Link>
        </div>

        <Card className="bg-[#141417] border-[#27272A]">
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl font-bold text-center"><h1>{t('auth.welcomeBack')}</h1></CardTitle>
            <CardDescription className="text-center text-zinc-400">
              {t('auth.enterCredentials')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">{t('auth.email')}</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder={t('auth.emailPlaceholder')}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="bg-[#0B0B0C] border-[#27272A] focus:border-[#FFC107]"
                  data-testid="login-email"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">{t('auth.password')}</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="bg-[#0B0B0C] border-[#27272A] focus:border-[#FFC107] pr-10"
                    data-testid="login-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-white"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full h-12 bg-[#FFC107] text-black hover:bg-[#F59E0B] font-bold uppercase tracking-wide"
                data-testid="login-submit"
              >
                {loading ? t('auth.signingIn') : t('auth.signIn')}
              </Button>
            </form>

            <div className="mt-6 text-center text-sm">
              <span className="text-zinc-400">{t('auth.noAccount')} </span>
              <Link to="/register" className="text-[#FFC107] hover:underline font-medium" data-testid="register-link">
                {t('auth.signUp')}
              </Link>
            </div>
            <div className="mt-3 text-center">
              <Link to="/menu" className="text-zinc-500 hover:text-[#FFC107] text-xs transition-colors">
                {t('nav.menu')}
              </Link>
              <span className="text-zinc-700 mx-2">|</span>
              <Link to="/" className="text-zinc-500 hover:text-[#FFC107] text-xs transition-colors">
                {t('nav.home')}
              </Link>
            </div>

          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default LoginPage;
